import { createSelector } from '@reduxjs/toolkit';
import {
  selectCurrentSchoolData,
  selectCurrentSchoolHref,
} from '@store/userAndSchool/userAndSchoolSelectors';
import { sortBy } from 'lodash-es';
import { streamsOrderMap } from '../../constants/apiConstants';
import { getGradeForStudyProgramme } from '@store/curriculum/getGradeForStudyProgramme';
import { createTypedSelector } from '@store/genericHelpers';
import type { CustomStudyProgrammeGroup } from '../../types/llinkidApiTypes';

export const selectIsStudyProgrammeDataLoaded = (state) => {
  return (
    !state.studyProgrammeApiData.isLoading && state.studyProgrammeApiData.allPrograms.length > 0
  );
};

const selectStudyProgrammesForSchool = createTypedSelector(
  [
    (state) => state.studyProgrammeApiData.allPrograms,
    (state) => state.userAndSchools.schoolsData?.[selectCurrentSchoolHref(state)]?.studyProgrammes,
  ],
  (allPrograms, schoolProgrammes) => {
    return allPrograms.filter((program) => {
      return schoolProgrammes?.some((schoolProgram) => schoolProgram.key === program.key);
    });
  },
);

export const selectStudyProgrammesPerGradeForSchool = createTypedSelector(
  [selectStudyProgrammesForSchool, (state) => state.studyProgrammeApiData.allGrades],
  (schoolPrograms, allGrades) => {
    const programmesPerGrade: Record<string, { name: string; value: string }[]> = {};

    schoolPrograms.forEach((program) => {
      const grade = getGradeForStudyProgramme(program, allGrades);
      if (!grade) return;

      if (!programmesPerGrade[grade.$$meta.permalink]) {
        programmesPerGrade[grade.$$meta.permalink] = [];
      }
      programmesPerGrade[grade.$$meta.permalink].push({
        name: program.title,
        value: program.$$meta.permalink,
      });
    });

    return programmesPerGrade;
  },
);

export const selectStudyProgrammeGroupsPerGradeForSchool = createSelector(
  [
    selectStudyProgrammesPerGradeForSchool,
    (state) => selectCurrentSchoolData(state).studyProgrammeGroups,
  ],
  (studyProgrammesPerGrade, schoolProgrammeGroups) => {
    const programmeGroupsPerGrade = {};
    Object.keys(studyProgrammesPerGrade).forEach((grade) => {
      const studyProgrammeGroups: Array<{
        name: string;
        description: string;
        value: null | string;
        studyProgrammes: string[];
      }> = [
        {
          name: 'Voor de hele graad',
          description: 'Maak keuzes voor de hele graad.',
          value: null,
          studyProgrammes: studyProgrammesPerGrade[grade].map((s) => s.value),
        },
      ];
      sortBy(schoolProgrammeGroups, [(spg) => streamsOrderMap.get(spg.title)]).forEach((group) => {
        if (
          group.studyProgrammes.some((sp) =>
            studyProgrammesPerGrade[grade].some((sel) => sel.value === sp.href),
          )
        ) {
          studyProgrammeGroups.push({
            name: `Voor de ${group.title}`,
            description: `Maak keuzes voor de ${group.title}.`,
            value: group.$$meta.permalink,
            studyProgrammes: group.studyProgrammes.map((s) => s.href),
          });
        }
      });
      programmeGroupsPerGrade[grade] = studyProgrammeGroups;
    });

    return programmeGroupsPerGrade;
  },
);

export const selectAllGrades = createSelector(
  [(state) => state.studyProgrammeApiData.allGrades],
  (allGrades) => {
    return allGrades.map((grade) => ({
      name: grade.title,
      value: grade.$$meta.permalink,
    }));
  },
);

export const selectAllProgrammes = createSelector(
  [
    (state) => state.studyProgrammeApiData.allPrograms,
    (state) => state.studyProgrammeApiData.allGrades,
  ],
  (allPrograms, allGrades) => {
    return allPrograms.map((program) => {
      const grade = getGradeForStudyProgramme(program, allGrades);
      const gradeString = grade
        ? `(${grade.title}${grade.title === '1' ? 'ste' : 'de'} graad)`
        : '';
      return {
        name: `${program.title} ${gradeString}`,
        value: program.$$meta.permalink,
      };
    });
  },
);

export const selectStudyProgrammesFromKeys = (state, selectablePrograms, studyProgram) => {
  const currentSchool = selectCurrentSchoolData(state);
  const studyPrograms: string[] = [];
  const selectableProgramHrefs = selectablePrograms.map((e) => e.value);
  studyProgram.forEach((sp) => {
    if (sp.startsWith('/llinkid/customstudyprogrammegroups')) {
      // /we have a studyprogrammegroup
      const group = currentSchool.studyProgrammeGroups.find(
        (e) => e.$$meta.permalink === sp,
      ) as CustomStudyProgrammeGroup;
      group.studyProgrammes.forEach((gsp) => {
        if (selectableProgramHrefs.includes(gsp.href)) {
          studyPrograms.push(gsp.href);
        }
      });
    } else if (sp.startsWith('/sam/')) {
      studyPrograms.push(sp);
    }
  });
  return studyPrograms;
};

export const selectAllStreamsAndFinalities = createSelector(
  [
    (state) => state.studyProgrammeApiData.firstGradeStreams,
    (state) => state.studyProgrammeApiData.secondGradeFinalities,
  ],
  (firstGradeStreams, secondGradeFinalities) => {
    const streamsAndFinalities: Array<{ name: string; value: string }> = [];
    [...firstGradeStreams, ...secondGradeFinalities].forEach((stream) => {
      streamsAndFinalities.push({
        name: stream.title,
        value: stream.$$meta.permalink,
      });
    });
    return streamsAndFinalities;
  },
);
